import { useContext, useEffect, useMemo } from "react";
import { useTable, useSortBy, SortingRule, useFlexLayout, PluginHook, Column } from "react-table";
import { TableHeader } from "./TableHeader";
import { TableBody } from "./TableBody";
import { Loading, NoResults } from "modules/common";
import { AccessibilityContext } from "views/AccessibilityContext";

export enum TableType {
    EducationsTable = "education-table",
    RecordsTable = "records-table",
    OuterModuleTable = "outer-module-table",
    AdministrationTable = "administration-table",
    MessageTemplatesTable = "message-template-table",
    SentNotificationsTable = "sent-notifications-table",
    UserNotificationsTable = "user-notifications-table",
    NotificationTemplatesTable = "notification-templates-table",
}

export type TableColumn<T extends object> = Column<T> & {
    className?: string;
};

interface Props {
    data: Array<any>;
    columns: TableColumn<any>[];
    loading?: boolean;
    flex?: boolean;
    setSortBy?: (sortBy: SortingRule<object>) => void;
    tableType: TableType;
    disableSort?: boolean;
}

export const CustomTable: React.FC<Props> = ({
    data,
    columns,
    loading,
    flex,
    setSortBy,
    tableType,
    disableSort,
}) => {
    const { fontSize } = useContext(AccessibilityContext);
    const tablePlugins = useMemo(() => {
        const plugins: PluginHook<object>[] = [useSortBy];
        if (flex) plugins.push(useFlexLayout);
        return plugins;
    }, [flex]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        state: { sortBy },
    } = useTable(
        {
            columns,
            data,
            manualSortBy: true,
        },
        ...tablePlugins
    );

    useEffect(() => {
        if (setSortBy) {
            setSortBy(sortBy[0]);
        }
    }, [sortBy, setSortBy]);

    if (loading) {
        return <Loading />;
    }

    return (
        <table
            className={`table ${flex ? "flex" : ""}`}
            {...getTableProps()}
        >
            <TableHeader
                headerGroups={headerGroups}
                sortBy={sortBy?.[0]}
                tableType={tableType}
                disableSort={disableSort}
            />
            {data.length === 0 ? (
                <tbody>
                    <tr>
                        <td colSpan={columns.length} className="no-results">
                            <NoResults className="mt-24" />
                        </td>
                    </tr>
                </tbody>
            ) : (
                <TableBody
                    rows={rows}
                    getTableBodyProps={getTableBodyProps}
                    prepareRow={prepareRow}
                    tableType={tableType}
                    fontSize={fontSize}
                />
            )}
        </table>
    );
};
